import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Search, Building2, Shield, AlertTriangle, CheckCircle2,
  ChevronRight, Trash2, ClipboardList, Loader2
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import { Switch } from "../components/ui/switch";
import { Separator } from "../components/ui/separator";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from "../components/ui/dialog";
import { toast } from "sonner";
import { PageHeader } from "../components/PageHeader";

const riskColors = {
  low: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  medium: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
  high: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  critical: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
};

const VendorRisk = () => {
  const { token } = useAuth();
  const { t } = useI18n();
  const [vendors, setVendors] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showAssess, setShowAssess] = useState(null);
  const [questionnaire, setQuestionnaire] = useState([]);
  const [responses, setResponses] = useState({});
  const [assessing, setAssessing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [newVendor, setNewVendor] = useState({
    name: "", category: "technology", contact_name: "", contact_email: "",
    services_provided: "", data_access_level: "limited", criticality: "medium"
  });

  const fetchAll = useCallback(async () => {
    try {
      const [vRes, sRes, qRes] = await Promise.all([
        axios.get(`${API}/vendors`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/vendors/summary`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/vendors/questionnaire`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setVendors(vRes.data);
      setSummary(sRes.data);
      setQuestionnaire(qRes.data);
    } catch (error) {
      console.error("Failed:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const createVendor = async () => {
    if (!newVendor.name) { toast.error("Vendor name required"); return; }
    try {
      await axios.post(`${API}/vendors`, newVendor, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Vendor added");
      setShowCreate(false);
      setNewVendor({ name: "", category: "technology", contact_name: "", contact_email: "", services_provided: "", data_access_level: "limited", criticality: "medium" });
      fetchAll();
    } catch (error) {
      toast.error("Failed to create vendor");
    }
  };

  const deleteVendor = async (vendorId) => {
    try {
      await axios.delete(`${API}/vendors/${vendorId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Vendor deleted");
      fetchAll();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const startAssessment = (vendor) => {
    setShowAssess(vendor);
    const existing = vendor.questionnaire_responses || {};
    setResponses(existing);
  };

  const submitAssessment = async () => {
    if (!showAssess) return;
    setAssessing(true);
    try {
      const res = await axios.post(`${API}/vendors/${showAssess.vendor_id}/assess`, responses, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Assessment complete. Risk: ${res.data.risk_level} (${res.data.risk_score}%)`);
      setShowAssess(null);
      fetchAll();
    } catch (error) {
      toast.error("Assessment failed");
    } finally {
      setAssessing(false);
    }
  };

  const filtered = vendors.filter(v =>
    !searchQuery || v.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="vendor-risk-page">
      <PageHeader
        title={t("vendor.title")}
        description={t("vendor.subtitle")}
        actions={
          <Button onClick={() => setShowCreate(true)} data-testid="add-vendor-btn">
            <Plus className="w-4 h-4 mr-2" /> {t("vendor.add")}
          </Button>
        }
      />

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="border-border"><CardContent className="pt-4 pb-4">
          <p className="text-2xl font-heading font-bold">{summary?.total || 0}</p>
          <p className="text-xs text-muted-foreground">{t("total")} {t("vendor.title").split(" ")[0]}</p>
        </CardContent></Card>
        {["low", "medium", "high", "critical"].map(level => (
          <Card key={level} className="border-border"><CardContent className="pt-4 pb-4">
            <p className={`text-2xl font-heading font-bold ${level === 'critical' ? 'text-red-500' : level === 'high' ? 'text-orange-500' : level === 'medium' ? 'text-yellow-500' : 'text-green-500'}`}>
              {summary?.by_risk_level?.[level] || 0}
            </p>
            <p className="text-xs text-muted-foreground capitalize">{level} Risk</p>
          </CardContent></Card>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input placeholder={t("vendor.search_ph")} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" data-testid="vendor-search" />
      </div>

      {/* Vendor List */}
      <div className="space-y-3">
        <AnimatePresence mode="popLayout">
          {filtered.map((vendor, i) => (
            <motion.div key={vendor.vendor_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2, delay: i * 0.02 }}>
              <Card className="border-border hover:border-primary/50 transition-colors" data-testid={`vendor-card-${vendor.vendor_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <Building2 className="w-8 h-8 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          {vendor.risk_level && <Badge variant="outline" className={riskColors[vendor.risk_level]}>{vendor.risk_level} risk</Badge>}
                          <Badge variant="secondary" className="capitalize">{vendor.criticality}</Badge>
                          <Badge variant="secondary" className="capitalize">{vendor.category}</Badge>
                        </div>
                        <p className="font-medium">{vendor.name}</p>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                          {vendor.contact_name && <span>{vendor.contact_name}</span>}
                          {vendor.risk_score !== null && <span>Score: {vendor.risk_score}%</span>}
                          {vendor.last_assessment_date && <span>Assessed: {new Date(vendor.last_assessment_date).toLocaleDateString()}</span>}
                          {!vendor.risk_score && <span className="text-orange-500">Not assessed</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="outline" size="sm" onClick={() => startAssessment(vendor)} data-testid={`assess-vendor-${vendor.vendor_id}`}>
                        <ClipboardList className="w-4 h-4 mr-1" /> {t("vendor.assess")}
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => deleteVendor(vendor.vendor_id)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </AnimatePresence>
        {filtered.length === 0 && (
          <div className="text-center py-12">
            <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">{vendors.length === 0 ? t("vendor.no_vendors_title") : t("no_data")}</p>
          </div>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{t("vendor.new_title")}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label>{t("vendor.name_label")} *</Label>
              <Input value={newVendor.name} onChange={(e) => setNewVendor({ ...newVendor, name: e.target.value })} placeholder="e.g., AWS, Cloudflare" data-testid="vendor-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("vendor.category_label")}</Label>
                <Select value={newVendor.category} onValueChange={(v) => setNewVendor({ ...newVendor, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="technology">{t("vendor.cat.technology")}</SelectItem>
                    <SelectItem value="cloud">{t("vendor.cat.cloud")}</SelectItem>
                    <SelectItem value="consulting">{t("vendor.cat.consulting")}</SelectItem>
                    <SelectItem value="outsourcing">{t("vendor.cat.outsourcing")}</SelectItem>
                    <SelectItem value="other">{t("other") || "Other"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("vendor.criticality_label")}</Label>
                <Select value={newVendor.criticality} onValueChange={(v) => setNewVendor({ ...newVendor, criticality: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">{t("low")}</SelectItem>
                    <SelectItem value="medium">{t("medium")}</SelectItem>
                    <SelectItem value="high">{t("high")}</SelectItem>
                    <SelectItem value="critical">{t("critical")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>{t("vendor.contact_name_label")}</Label><Input value={newVendor.contact_name} onChange={(e) => setNewVendor({ ...newVendor, contact_name: e.target.value })} /></div>
              <div className="space-y-2"><Label>{t("vendor.contact_email_label")}</Label><Input value={newVendor.contact_email} onChange={(e) => setNewVendor({ ...newVendor, contact_email: e.target.value })} /></div>
            </div>
            <div className="space-y-2">
              <Label>{t("vendor.data_access_label")}</Label>
              <Select value={newVendor.data_access_level} onValueChange={(v) => setNewVendor({ ...newVendor, data_access_level: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("vendor.access.none")}</SelectItem>
                  <SelectItem value="limited">{t("vendor.access.limited")}</SelectItem>
                  <SelectItem value="significant">{t("vendor.access.significant")}</SelectItem>
                  <SelectItem value="full">{t("vendor.access.full")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>{t("cancel")}</Button>
            <Button onClick={createVendor} data-testid="save-vendor-btn">{t("vendor.create_btn")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assessment Dialog */}
      <Dialog open={!!showAssess} onOpenChange={() => setShowAssess(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("vendor.questionnaire")}: {showAssess?.name}</DialogTitle>
            <DialogDescription>{t("vendor.questionnaire_desc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {questionnaire.map(q => (
              <div key={q.id} className="flex items-start gap-4 p-3 rounded-lg bg-muted/30">
                <Switch checked={responses[q.id] || false} onCheckedChange={(v) => setResponses({ ...responses, [q.id]: v })}
                  data-testid={`q-${q.id}`} />
                <div className="flex-1">
                  <p className="text-sm font-medium">{q.question}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="secondary" className="text-xs">{q.category}</Badge>
                    <span className="text-xs text-muted-foreground">{t("score")}: {q.weight}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssess(null)}>{t("cancel")}</Button>
            <Button onClick={submitAssessment} disabled={assessing} data-testid="submit-assessment-btn">
              {assessing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              {t("vendor.submit_assessment")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VendorRisk;
